import * as _ from 'lodash';

import { countBooleanValues } from '../utils';

export enum PasswordStrengthLabel {
  None,
  Poor,
  Medium,
  Strong,
  Excellent,
}

export type PasswordStrength = {
  label: PasswordStrengthLabel;
  isLongEnough: boolean;
  hasLowerCase: boolean;
  hasUpperCase: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
};

//  Allows us to express the rules as an object of rule methods AND the resulting object of booleans.
export type PasswordStrengthRules<T> = Record<keyof Omit<PasswordStrength, 'label'>, T>;

/**
 * Password Strength Rules
 * - At least 8 characters long.
 * - At least 1 lowercase character.
 * - At least 1 uppercase character.
 * - At least 1 digit character.
 * - At least 1 symbol character.
 */
export const MIN_PASSWORD_LENGTH = 8;

export const RULES: PasswordStrengthRules<(password: string) => boolean> = {
  isLongEnough: (password: string) => password.length >= MIN_PASSWORD_LENGTH,
  hasLowerCase: (password: string) => /[a-z]/.test(password),
  hasUpperCase: (password: string) => /[A-Z]/.test(password),
  hasNumber: (password: string) => /[\d]/.test(password),
  hasSymbol: (password: string) => /[^\p{L}\d\s]/u.test(password), // See: https://stackoverflow.com/questions/8359566/regex-to-match-symbols#54256858
};

/**
 * Returns the PasswordStrengthLabel value for a given set of boolean PasswordStrengthRules.
 *
 * @param {PasswordStrengthRules<boolean>} passwordRules - Object of password rule values.
 * @returns {PasswordStrengthLabel}
 */
export function getPasswordStrengthLabel(passwordRules: PasswordStrengthRules<boolean>): PasswordStrengthLabel {
  const validRulesCount: number = countBooleanValues(Object.values(passwordRules), true);
  switch (validRulesCount) {
    case 5:
      return PasswordStrengthLabel.Excellent;
    case 4:
      return PasswordStrengthLabel.Strong;
    case 3:
      return PasswordStrengthLabel.Medium;
    case 2:
    case 1:
      return PasswordStrengthLabel.Poor;
    case 0:
    default:
      return PasswordStrengthLabel.None;
  }
}

//  By default, empty passwords are None.
const defaultPasswordStrength: PasswordStrength = {
  label: PasswordStrengthLabel.None,
  isLongEnough: false,
  hasLowerCase: false,
  hasUpperCase: false,
  hasNumber: false,
  hasSymbol: false,
};

/**
 * Test a password's strength, based on our rules.
 *
 * @param {string} password - A given user password.
 * @returns {PasswordStrength}
 */
export function testPasswordStrength(password: string): PasswordStrength {
  //  Return None if no password
  if (!password) {
    return defaultPasswordStrength;
  }

  // Iterate through each rule, passing the password to the "tester" fn.
  // Returns a object of password strength booleans, representing the how "strong" the password is.
  const passwordRules: PasswordStrengthRules<boolean> = _.mapValues(RULES, tester => tester(password));
  return {
    ...passwordRules,
    label: getPasswordStrengthLabel(passwordRules),
  };
}
